import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  FaFileContract,
  FaPlus,
  FaSave,
  FaDownload,
  FaTrash,
  FaFolderOpen,
  FaMagic,
  FaArrowLeft,
  FaCheckDouble,
  FaUndo,
  FaSearchMinus,
  FaSearchPlus,
  FaFileSignature,
} from "react-icons/fa";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { fetchUsersByField, tolerantId } from "../../utils/firestoreHelpers";
import { generarPresupuestoPdf } from "../../utils/presupuestoPdf";
import ScreenHeader from "../../components/ScreenHeader";
import Loader from "../../components/Loader";
import AdminModal from "../../components/AdminModal";
import StatusBadge from "../../components/StatusBadge";
import { colors, spacing, typography, radius, shadows, transitions, btnPrimary, btnSecondary, btnDanger } from "../../utils/designTokens";
import ActaTemplate from "../../components/actas/ActaTemplate";
import ActaForm from "../../components/actas/ActaForm";
import ActaAiAssistant from "../../components/actas/ActaAiAssistant";
import ActaEstadoBanner from "../../components/actas/ActaEstadoBanner";
import ActaRevisionPanel from "../../components/actas/ActaRevisionPanel";
import ActaMinutaModal from "../../components/actas/ActaMinutaModal";
import ActaPublicarModal from "../../components/actas/ActaPublicarModal";
import { mmAPx } from "../../components/actas/actaPaginacion";
import { defaultActa } from "../../utils/actaConfig";
import {
  actaAdmin,
  cancelarProcesamiento,
  mensajeDeError,
  esConflictoDeVersion,
  esNumeroDesactualizado,
  esProcesamientoEnCurso,
  codigoDeError,
  detallesDeError,
  subirAUrlFirmada,
  urlDescargable,
} from "../../utils/asambleas/api";
import {
  actaDesdeAsamblea,
  actaParaEditor,
  armarPadronVivo,
  calcularResultadosSnapshot,
  dataParaGuardar,
  enColaLocal,
  fueraDelPadron,
  estadoActa,
  fechaCorta,
  fechaHora,
  marcasPorPunto,
  minutaBloqueaActa,
  minutaEnCurso,
  minutaParaDeshacer,
  normalizarDetalle,
  normalizarProcesamiento,
  normId,
  padronDesdeSnapshot,
  proximoNumeroActa,
  puedeCancelarProcesamiento,
  puedePublicarDe,
  revisarParaPublicar,
  marcasPendientes,
  estadoDeMarca,
  marcaCerrada,
  conEstadoDeMarca,
  workerApagado,
} from "../../components/actas/actaFlujo";

// Editor de Actas de Asamblea (Admin / SuperAdmin).
// Layout split: vista previa A4 a la izquierda; a la derecha el formulario y,
// si la redactó la IA, el panel de revisión (marcas + citas con "escuchar").
//
// Todo pasa por el callable actaAdmin: el cliente no lee ni escribe la
// colección `actas` (las reglas lo niegan). El padrón vivo (apartments +
// users) se sigue leyendo de Firestore para armar la asistencia.
//
// Si el audio de la asamblea quedó en cola con el procesamiento automático
// apagado, el servidor traba el acta (guardar y publicar responden
// "procesando") y nada lo destraba solo: se avisa y se ofrece "Cancelar
// procesamiento" (actaAdmin cancelarProcesamiento).
//
// Uso:
//   pantalla "actas":            <ActasEditor />  (elige edificio, lista, nueva)
//   desde el detalle de asamblea: <ActasEditor actaId="asm_x" asamblea={a} onVolver={fn} />
//     (sin actaId arranca un acta manual para esa asamblea)

const POLL_MINUTA_MS = 4000;
const MINUTA_MAX_ESPERA_MS = 20 * 60 * 1000;
const MAX_FALLOS_CONSULTA = 5;
const MAX_PDF_BYTES = 15 * 1024 * 1024;
// El acta firmada es un escaneo: el servidor acepta hasta 30 MB.
const MAX_FIRMADA_BYTES = 30 * 1024 * 1024;
// Si otra acta del edificio se publica en el medio, publicar rechaza el número
// impreso: se regenera el PDF con el que corresponde, hasta esta cantidad de veces.
const MAX_INTENTOS_NUMERO = 3;

const AVISO_CONFLICTO =
  "Alguien más guardó cambios en esta acta (o se aplicó una minuta) mientras la editabas. Cargamos la última versión; lo que no habías guardado se descartó.";
const AVISO_MINUTA_EN_COLA =
  "La minuta quedó en cola: el procesamiento automático todavía no está activo y se aplica cuando se habilite. Mientras tanto podés seguir editando el acta.";
const AVISO_PROCESAMIENTO_EN_COLA =
  "El audio de esta asamblea quedó en cola para que la IA redacte el acta, pero el procesamiento automático no está activo para el edificio. Mientras siga en cola, el acta no se puede guardar ni publicar: si vas a seguir a mano, cancelá el procesamiento.";

/**
 * Padrón vivo del edificio (apartments + users) con las mismas reglas que el
 * padrón que congela el servidor al publicar: sin portería, mismas etiquetas,
 * mismo orden y los mismos docs de users. Así el PDF y la vista del acta
 * publicada coinciden.
 *
 * El id del doc va DESPUÉS de los datos (como servicio.queryIn del servidor):
 * un campo `id` guardado en el doc (con las reglas viejas cualquier sesión lo
 * podía escribir) no puede pisar el id real; si no, esa unidad tendría en el
 * editor un id distinto del que congela padronSnapshot.
 */
async function cargarPadronVivo(building) {
  const aptSnap = await getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(building.buildingid))));
  const apartments = aptSnap.docs.map((d) => ({ ...d.data(), id: d.id })).filter((apt) => !fueraDelPadron(apt));
  const ownerIds = new Set();
  apartments.forEach((apt) => {
    for (const campo of ["owner", "ownerSecondary"]) {
      const id = normId(apt[campo]);
      if (id) ownerIds.add(id);
    }
  });
  // Docs CRUDOS de users, sin deduplicar (como cargarPadron del servidor): quien
  // es propietario y también staff tiene un doc por typeuser, y puede tener
  // fichas archivadas. Elige armarPadronVivo con las reglas del servidor
  // (archivedAt, isDemo y "gana el de residente"); fetchUsersByUserIds se queda
  // con una ficha por userid y el nombre impreso podía no ser el congelado.
  // Reglas 0D (C10): el staff lee fichas ajenas solo con accessBids del
  // edificio. fetchUsersByField fija where("accessBids", "array-contains", bid)
  // y devuelve todos los docs ({ ...datos, id }: el id del doc gana) en lotes
  // de 30 valores.
  const valores = [];
  const vistos = new Set(); // number y string de cada id, sin repetir valores en los `in`
  for (const id of ownerIds) {
    for (const v of tolerantId(id)) {
      const clave = `${typeof v}:${v}`;
      if (!vistos.has(clave)) {
        vistos.add(clave);
        valores.push(v);
      }
    }
  }
  const users = valores.length > 0 ? await fetchUsersByField("userid", valores, building.buildingid) : [];
  return armarPadronVivo(building, apartments, users);
}

function nombrePdf(acta, numero) {
  const safe = String((acta && acta.edificioNombre) || "Acta").replace(/[^\w\sáéíóúÁÉÍÓÚñÑ-]/g, "").trim() || "Acta";
  return `Acta de Asamblea - ${safe}${numero ? ` - ${numero}` : ""}.pdf`;
}

// Dos frames: React ya pintó el número nuevo antes de capturar el PDF.
const esperarPintado = () =>
  new Promise((resolve) => {
    if (typeof window.requestAnimationFrame !== "function") return setTimeout(resolve, 0);
    return window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
  });

// Zoom de la vista previa: "ancho" (la hoja ocupa el panel), "pagina" (entra
// la hoja entera) o un número fijo. El PDF siempre sale sin zoom.
const ZOOM_MIN = 0.35;
const ZOOM_MAX = 1.5;
const ANCHO_HOJA_PX = mmAPx(210);
const ALTO_HOJA_CON_MARGEN_PX = mmAPx(297 + 14 * 2);
const acotarZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

function zoomEfectivo(modo, tam) {
  if (typeof modo === "number") return acotarZoom(modo);
  if (!tam || !tam.w) return 1;
  const ancho = (tam.w - 32) / ANCHO_HOJA_PX;
  if (modo === "pagina") return acotarZoom(Math.min(ancho, (tam.h - 56) / ALTO_HOJA_CON_MARGEN_PX));
  return acotarZoom(ancho);
}

const metaNueva = (asambleaId) => ({
  id: null,
  asambleaId: asambleaId || null,
  status: "borrador",
  version: 0,
  actaNumero: null,
  origen: "manual",
  padronSnapshot: null,
  publicadaEnMs: null,
  envioActa: null,
  updatedAtMs: null,
});

export default function ActasEditor({ actaId: actaIdInicial = null, asamblea = null, onVolver = null }) {
  const { selectedBuilding, buildings, setSelectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const templateRef = useRef(null);
  const abiertoParaRef = useRef(null); // padrón para el que ya se abrió el acta inicial
  const embebido = typeof onVolver === "function";

  const [padron, setPadron] = useState(null); // { edificio, copropietarios } vivo
  const [padronCongelado, setPadronCongelado] = useState(null); // el de la publicación
  const [cargandoPadron, setCargandoPadron] = useState(false);
  const [lista, setLista] = useState([]);
  const [meta, setMeta] = useState(null);
  const [acta, setActa] = useState(null);
  const [baseline, setBaseline] = useState(null); // JSON de lo último guardado o cargado
  const [revision, setRevision] = useState(null);
  // JSON de la revisión guardada: el seguimiento de las marcas (ticks y cerradas) se guarda con el acta.
  const [revisionBaseline, setRevisionBaseline] = useState(null);
  const [ia, setIa] = useState(null);
  const [abriendo, setAbriendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [showList, setShowList] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [tab, setTab] = useState("editar");
  const [confirmar, setConfirmar] = useState(null); // { titulo, texto, etiqueta, peligro, accion }
  const [avisoConflicto, setAvisoConflicto] = useState("");
  const [minutaOpen, setMinutaOpen] = useState(false);
  const [minuta, setMinuta] = useState(null);
  const [deshaciendo, setDeshaciendo] = useState(false);
  const [publicarOpen, setPublicarOpen] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [progresoPub, setProgresoPub] = useState(null);
  const [numeroPrevisto, setNumeroPrevisto] = useState("");
  // N° que se pinta en la vista previa SOLO mientras se genera el PDF a publicar:
  // no se guarda en el acta (lo fija el servidor al publicar).
  const [numeroImpresion, setNumeroImpresion] = useState(null);
  // Copia del documento sin zoom, fuera de pantalla, montada solo mientras se genera el PDF.
  const [capturando, setCapturando] = useState(false);
  const [zoomModo, setZoomModo] = useState("ancho");
  const [panelTam, setPanelTam] = useState(null);
  const observadorRef = useRef(null);
  const previewRef = useCallback((nodo) => {
    if (observadorRef.current) observadorRef.current.disconnect();
    observadorRef.current = null;
    if (!nodo || typeof window.ResizeObserver !== "function") return;
    const ro = new window.ResizeObserver((entradas) => {
      const r = entradas[0] && entradas[0].contentRect;
      if (r) setPanelTam({ w: r.width, h: r.height });
    });
    ro.observe(nodo);
    observadorRef.current = ro;
  }, []);
  const zoom = zoomEfectivo(zoomModo, panelTam);
  // Revisiones del acta (detalle): con ellas "Deshacer" la última minuta sobrevive a recargar.
  const [revisiones, setRevisiones] = useState(null);
  const [cargandoMinuta, setCargandoMinuta] = useState(false);
  // ¿El edificio tiene Asambleas habilitado? (null = el servidor no lo dijo: como antes, se ofrece publicar).
  const [puedePublicarSrv, setPuedePublicarSrv] = useState(null);
  // Procesamiento del audio de la asamblea del acta (solo para ofrecer "Cancelar
  // procesamiento" si quedó en cola con el worker apagado: así el acta queda trabada).
  const [procAsamblea, setProcAsamblea] = useState(null);
  const [cancelandoProc, setCancelandoProc] = useState(false);

  const buildingId = selectedBuilding ? selectedBuilding.buildingid : null;
  const publicada = !!meta && meta.status === "publicada";
  const puedePublicar = puedePublicarSrv !== false;
  const minutaActiva = minutaBloqueaActa(minuta);
  const minutaEnCola = minutaEnCurso(minuta) && !minutaActiva; // en cola con el worker apagado
  const soloLectura = publicada || minutaActiva || publicando;
  const padronVista = padronCongelado || padron;
  const revisionCambiada = !publicada && revisionBaseline != null && JSON.stringify(revision || null) !== revisionBaseline;
  const dirty = useMemo(
    () => !!acta && !publicada && baseline != null && (JSON.stringify(acta) !== baseline || revisionCambiada),
    [acta, baseline, publicada, revisionCambiada]
  );
  const actaVista = useMemo(
    () => (acta && numeroImpresion ? { ...acta, actaNumero: numeroImpresion } : acta),
    [acta, numeroImpresion]
  );
  // En el formulario solo las marcas que siguen pendientes (ni resueltas ni cerradas).
  const marcasMap = useMemo(
    () =>
      publicada
        ? null
        : marcasPorPunto({ marcas: ((revision && revision.marcas) || []).filter((m) => m && estadoDeMarca(m) !== "resuelta" && !marcaCerrada(m)) }),
    [revision, publicada]
  );
  const nMarcas = publicada ? 0 : marcasPendientes(revision);
  const cambiarMarca = useCallback((indice, cambios) => setRevision((r) => conEstadoDeMarca(r, indice, cambios)), []);
  const tieneRevision = !!(revision || ia) && !!(meta && meta.asambleaId);
  const deshacible = useMemo(
    () => (publicada || !meta ? null : minutaParaDeshacer(revisiones, meta.version)),
    [revisiones, meta, publicada]
  );
  // Sin revisiones del servidor no se sabe: se ofrece y el servidor valida.
  const puedeDeshacerMinuta =
    revisiones == null || !!(deshacible && minuta && minuta.revisionId && deshacible.revisionId === minuta.revisionId);

  // ── Datos ──
  const cargarLista = useCallback(async () => {
    if (buildingId == null) return null;
    try {
      const res = await actaAdmin("listar", { buildingId });
      const actas = ((res && res.actas) || []).slice().sort((x, y) => (y.updatedAtMs || 0) - (x.updatedAtMs || 0));
      setLista(actas);
      const pp = puedePublicarDe(res);
      if (pp !== null) setPuedePublicarSrv(pp);
      return actas;
    } catch (e) {
      showToast(`No se pudo cargar la lista de actas. ${mensajeDeError(e)}`, "error");
      return null;
    }
  }, [buildingId, showToast]);

  /**
   * Estado del procesamiento de la asamblea `aid` (o null). Best effort: si
   * falla (sin documento todavía, red) no hay nada que ofrecer.
   */
  const consultarProcesamiento = useCallback(async (aid) => {
    if (!aid) {
      setProcAsamblea(null);
      return null;
    }
    try {
      const p = normalizarProcesamiento(await actaAdmin("estadoProcesamiento", { aid }));
      setProcAsamblea({ ...p, aid });
      return p;
    } catch (_) {
      setProcAsamblea(null);
      return null;
    }
  }, []);

  const mostrarActa = useCallback((m, a, rev, iaRes, congelado, revs = null) => {
    setMeta(m);
    setActa(a);
    setBaseline(JSON.stringify(a));
    setRevision(rev);
    setRevisionBaseline(JSON.stringify(rev || null));
    setIa(iaRes);
    setPadronCongelado(congelado);
    setRevisiones(revs);
  }, []);

  /**
   * Carga un acta del servidor (pisa el estado local). Devuelve la meta o null.
   * `recarga`: misma acta (conflicto, minuta, deshacer, publicación): no cambia de pestaña.
   */
  const abrir = useCallback(
    async (actaId, { recarga = false } = {}) => {
      if (!padron || !actaId) return null;
      setAbriendo(true);
      try {
        const res = await actaAdmin("detalle", { actaId });
        const { meta: m, data, revision: rev, ia: iaRes, revisiones: revs, puedePublicar: pp } = normalizarDetalle(res, actaId);
        if (pp !== null) setPuedePublicarSrv(pp);
        const congelado =
          m.status === "publicada" && m.padronSnapshot
            ? padronDesdeSnapshot(
                m.padronSnapshot,
                {
                  id: padron.edificio.id,
                  nombre: data.edificioNombre || padron.edificio.nombre,
                  padron: data.padron || padron.edificio.padron,
                },
                padron
              )
            : null;
        const a = actaParaEditor(data, (congelado || padron).edificio, m);
        mostrarActa(m, a, rev, iaRes, congelado, revs);
        if (!recarga) {
          setTab(marcasPendientes(rev) > 0 && m.status !== "publicada" ? "revision" : "editar");
          setShowList(false);
        }
        // Un procesamiento en cola con el worker apagado traba esta acta: se mira aparte.
        if (m.asambleaId && m.status !== "publicada") consultarProcesamiento(m.asambleaId);
        else setProcAsamblea(null);
        return m;
      } catch (e) {
        showToast(mensajeDeError(e), "error");
        return null;
      } finally {
        setAbriendo(false);
      }
    },
    [padron, showToast, mostrarActa, consultarProcesamiento]
  );

  const nuevaLocal = useCallback(() => {
    if (!padron) return;
    const a = asamblea ? actaDesdeAsamblea(asamblea, padron.edificio) : defaultActa(padron.edificio);
    mostrarActa(metaNueva(asamblea && asamblea.id), a, null, null, null);
    setMinuta(null);
    setProcAsamblea(null); // crear no lo traba un procesamiento en cola
    setTab("editar");
    setShowList(false);
    setAvisoConflicto("");
  }, [padron, asamblea, mostrarActa]);

  // Padrón vivo del edificio seleccionado.
  useEffect(() => {
    setPuedePublicarSrv(null);
    if (!selectedBuilding) {
      setPadron(null);
      setActa(null);
      setMeta(null);
      setLista([]);
      return undefined;
    }
    let cancelado = false;
    (async () => {
      setCargandoPadron(true);
      try {
        const built = await cargarPadronVivo(selectedBuilding);
        if (!cancelado) setPadron(built);
      } catch (e) {
        // Sin datos del padrón en el log: solo el código del error.
        console.error("ActasEditor: no se pudo cargar el padrón", codigoDeError(e) || (e && e.name));
        if (!cancelado) showToast("No se pudo cargar el padrón del edificio.", "error");
      } finally {
        if (!cancelado) setCargandoPadron(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // Con el padrón listo: lista de actas y acta inicial (la pedida o una nueva).
  useEffect(() => {
    if (!padron || abiertoParaRef.current === padron) return;
    const primera = abiertoParaRef.current === null;
    abiertoParaRef.current = padron;
    cargarLista();
    setMinuta(null);
    setAvisoConflicto("");
    if (primera && actaIdInicial) {
      abrir(actaIdInicial).then((m) => {
        if (!m) nuevaLocal();
      });
    } else {
      nuevaLocal();
    }
  }, [padron, actaIdInicial, abrir, cargarLista, nuevaLocal]);

  // Aviso del navegador si se cierra la pestaña con cambios sin guardar o publicando.
  useEffect(() => {
    if (!dirty && !publicando) return undefined;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty, publicando]);

  // ── Guardar ──
  /**
   * El servidor rechazó por "procesando": puede ser un procesamiento en cola
   * que no va a correr (worker apagado). Se vuelve a mirar para ofrecer cancelarlo.
   */
  const revisarSiTrabaProcesamiento = (e) => {
    if (meta && meta.asambleaId && detallesDeError(e).motivo === "procesando") consultarProcesamiento(meta.asambleaId);
  };

  /**
   * Guarda `a` (crea el acta si todavía no existe). Devuelve { id, version }
   * o null; si chocó con otra edición, recarga la última versión y avisa.
   */
  const guardarData = async (a, { silencioso = false } = {}) => {
    if (!a || buildingId == null) return null;
    setGuardando(true);
    const enviadoJson = JSON.stringify(a);
    const revisionJson = JSON.stringify(revision || null);
    const conRevision = !!revision && revisionBaseline != null && revisionJson !== revisionBaseline;
    try {
      let ref;
      if (!meta || !meta.id) {
        const res = await actaAdmin("crear", {
          buildingId,
          ...(meta && meta.asambleaId ? { asambleaId: meta.asambleaId } : {}),
          data: dataParaGuardar(a),
        });
        if (!res || !res.actaId) throw new Error("El servidor no devolvió el acta creada.");
        ref = { id: res.actaId, version: Number(res.version) || 1 };
        setMeta((m) => ({ ...(m || metaNueva(null)), id: ref.id, version: ref.version, updatedAtMs: Date.now() }));
      } else {
        const res = await actaAdmin("guardar", {
          actaId: meta.id,
          version: meta.version,
          data: dataParaGuardar(a),
          ...(conRevision ? { revision } : {}),
        });
        const v = Number(res && res.version);
        ref = { id: meta.id, version: Number.isFinite(v) ? v : (Number(meta.version) || 0) + 1 };
        setMeta((m) => ({ ...m, version: ref.version, updatedAtMs: Date.now() }));
      }
      setBaseline(enviadoJson);
      if (meta && meta.id) setRevisionBaseline(revisionJson);
      setAvisoConflicto("");
      cargarLista();
      if (!silencioso) showToast("Acta guardada.", "success");
      return ref;
    } catch (e) {
      if (meta && meta.id && esConflictoDeVersion(e)) {
        await abrir(meta.id, { recarga: true });
        setAvisoConflicto(AVISO_CONFLICTO);
        showToast("El acta cambió mientras la editabas: cargamos la última versión.", "warning");
        return null;
      }
      if ((!meta || !meta.id) && meta && meta.asambleaId && codigoDeError(e) === "already-exists") {
        await abrir(`asm_${meta.asambleaId}`);
        showToast("Esta asamblea ya tenía un acta: la abrimos.", "warning");
        return null;
      }
      revisarSiTrabaProcesamiento(e);
      showToast(mensajeDeError(e), "error");
      return null;
    } finally {
      setGuardando(false);
    }
  };

  // ── Procesamiento en cola sin worker ──
  const pedirCancelarProcesamiento = () => {
    const aid = meta && meta.asambleaId;
    if (!aid || cancelandoProc) return;
    setConfirmar({
      titulo: "Cancelar el procesamiento del audio",
      texto:
        "El audio sale de la cola y la IA no va a redactar el acta. Los audios quedan subidos: los podés volver a procesar cuando se habilite el procesamiento automático. Mientras tanto, el acta se edita y se publica a mano.",
      etiqueta: "Cancelar procesamiento",
      peligro: true,
      accion: async () => {
        setCancelandoProc(true);
        try {
          await cancelarProcesamiento(aid);
          showToast("Procesamiento cancelado: ya podés guardar y publicar el acta.", "success");
        } catch (e) {
          showToast(
            esProcesamientoEnCurso(e)
              ? "El procesamiento ya arrancó y no se puede cancelar: esperá a que termine."
              : mensajeDeError(e),
            esProcesamientoEnCurso(e) ? "warning" : "error"
          );
        } finally {
          setCancelandoProc(false);
          await consultarProcesamiento(aid);
        }
      },
    });
  };

  const guardar = () => {
    if (soloLectura || guardando) return;
    guardarData(acta);
  };

  /** Para la minuta: el acta tiene que estar guardada (la minuta parte de la versión del servidor). */
  const asegurarGuardado = async () => {
    if (!meta || !meta.id) return false;
    if (!dirty) return true;
    return !!(await guardarData(acta, { silencioso: true }));
  };

  // ── Navegación con cambios sin guardar ──
  const conGuardia = (accion, texto) => {
    if (!dirty) {
      accion();
      return;
    }
    setConfirmar({
      titulo: "Tenés cambios sin guardar",
      texto: texto || "Si seguís, se pierden los cambios que no guardaste.",
      etiqueta: "Descartar cambios",
      peligro: true,
      accion,
    });
  };

  const handleSelectBuilding = (bid) => {
    const b = (buildings || []).find((x) => String(x.buildingid) === String(bid));
    if (!b) return;
    conGuardia(() => {
      setActa(null);
      setMeta(null);
      setSelectedBuilding(b);
    });
  };

  // ── Eliminar / descartar ──
  const pedirEliminar = (row) => {
    const enRevision = row.status === "en_revision";
    setConfirmar({
      titulo: enRevision ? "Descartar el acta en revisión" : "Eliminar el acta",
      texto: enRevision
        ? "El acta vuelve a borrador. Después la podés eliminar o volver a procesar el audio de la asamblea."
        : "Se elimina el borrador del acta. No se puede deshacer.",
      etiqueta: enRevision ? "Descartar" : "Eliminar",
      peligro: true,
      accion: async () => {
        try {
          await actaAdmin("eliminar", { actaId: row.id });
          const actas = (await cargarLista()) || [];
          if (meta && meta.id === row.id) {
            if (actas.some((x) => x.id === row.id)) await abrir(row.id);
            else nuevaLocal();
          }
          showToast(enRevision ? "El acta volvió a borrador." : "Acta eliminada.", "success");
        } catch (e) {
          if (meta && meta.id === row.id) revisarSiTrabaProcesamiento(e);
          showToast(mensajeDeError(e), "error");
        }
      },
    });
  };

  // ── PDF ──
  // html2canvas no respeta el `zoom` de la vista previa: el PDF se captura de
  // una copia sin zoom que se monta fuera de pantalla solo para esto.
  const conCaptura = async (fn) => {
    flushSync(() => setCapturando(true));
    try {
      await esperarPintado();
      if (!templateRef.current) throw new Error("No se pudo preparar el PDF.");
      return await fn(templateRef.current);
    } finally {
      setCapturando(false);
    }
  };

  const descargarPdf = async ({ firmada = false } = {}) => {
    if (generando || !acta) return;
    if (publicada && meta && meta.id) {
      // La ventana se abre en el click (antes del await) para que el navegador no la bloquee.
      const w = window.open("", "_blank");
      if (!w) {
        showToast("El navegador bloqueó la ventana del PDF: habilitá las ventanas emergentes para Habitia.", "warning");
        return;
      }
      w.opener = null;
      setGenerando(true);
      try {
        const res = await actaAdmin("pdfUrl", { actaId: meta.id, ...(firmada ? { firmada: true } : {}) });
        if (!res || !res.url) throw new Error("No encontramos el PDF publicado.");
        // Con headers (modo local) se baja con fetch y se abre como blob.
        w.location.href = await urlDescargable(res);
      } catch (e) {
        w.close();
        showToast(mensajeDeError(e), "error");
      } finally {
        setGenerando(false);
      }
      return;
    }
    setGenerando(true);
    try {
      await conCaptura((el) => generarPresupuestoPdf(el, nombrePdf(acta, acta.actaNumero), { download: true }));
    } catch (e) {
      showToast("No se pudo generar el PDF.", "error");
    } finally {
      setGenerando(false);
    }
  };

  // ── Acta firmada ──
  // Sobre un acta publicada, el escaneo con las firmas: desde que se registra,
  // es la que ven los propietarios (la generada queda). Cada reemplazo es una
  // versión nueva; no se avisa a nadie.
  const firmadaRef = useRef(null);
  const [subiendoFirmada, setSubiendoFirmada] = useState(false);
  const subirFirmada = async (file) => {
    if (!file || !meta || !meta.id || subiendoFirmada) return;
    if (file.type && file.type !== "application/pdf") {
      showToast("El acta firmada tiene que ser un PDF.", "error");
      return;
    }
    if (file.size > MAX_FIRMADA_BYTES) {
      showToast("El PDF es demasiado grande (hasta 30 MB).", "error");
      return;
    }
    setSubiendoFirmada(true);
    try {
      const sub = await actaAdmin("firmadaSubida", { actaId: meta.id });
      if (!sub || !sub.uploadUrl || !sub.path) throw new Error("El servidor no devolvió dónde subir el acta firmada.");
      await subirAUrlFirmada(sub.uploadUrl, file, { contentType: "application/pdf", headers: sub.headers || {} });
      const res = await actaAdmin("registrarFirmada", { actaId: meta.id, path: sub.path });
      setMeta((m) => ({ ...m, firmada: (res && res.firmada) || m.firmada }));
      showToast("Acta firmada guardada: los propietarios ya ven esta versión.", "success");
    } catch (e) {
      showToast(mensajeDeError(e), "error");
    } finally {
      setSubiendoFirmada(false);
    }
  };

  // ── Minuta ──
  // El seguimiento vive acá (no en el modal) para que siga si se cierra el modal.
  // Con el worker apagado no se sigue (quedaría consultando y con el acta
  // bloqueada sin que nada avance): se avisa y el acta se sigue editando.
  useEffect(() => {
    if (!minuta || !minutaBloqueaActa(minuta) || !meta || !meta.id) return undefined;
    let cancelado = false;
    const actaId = meta.id;
    const t = setTimeout(async () => {
      if (Date.now() - (minuta.desdeMs || Date.now()) > MINUTA_MAX_ESPERA_MS) {
        setMinuta((m) => m && {
          ...m,
          estado: "error",
          ultimoError: "sigue en cola después de 20 minutos. Volvé a abrir el acta más tarde para ver si se aplicó.",
        });
        return;
      }
      try {
        const res = (await actaAdmin("estadoMinuta", { actaId, minutaId: minuta.minutaId })) || {};
        if (cancelado) return;
        const apagado = workerApagado(res);
        setMinuta((m) => m && { ...m, ...res, minutaId: m.minutaId, fallosConsulta: 0, ...(apagado ? { workerApagado: true } : {}) });
        if (apagado) {
          showToast(AVISO_MINUTA_EN_COLA, "warning");
        } else if (res.estado === "aplicada") {
          await abrir(actaId, { recarga: true });
          const n = Array.isArray(res.aplicadas) ? res.aplicadas.length : 0;
          showToast(n ? `La minuta aplicó ${n} ${n === 1 ? "cambio" : "cambios"} al acta.` : "La minuta no generó cambios.", "success");
          setMinutaOpen(true);
        } else if (res.estado === "error") {
          showToast("No se pudo aplicar la minuta.", "error");
          setMinutaOpen(true);
        }
      } catch (e) {
        if (cancelado) return;
        setMinuta((m) => {
          if (!m) return m;
          const fallos = (m.fallosConsulta || 0) + 1;
          return fallos >= MAX_FALLOS_CONSULTA
            ? { ...m, estado: "error", ultimoError: "no pudimos consultar su estado. Volvé a abrir el acta en unos minutos." }
            : { ...m, fallosConsulta: fallos };
        });
      }
    }, POLL_MINUTA_MS);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [minuta, meta, abrir, showToast]);

  /** La minuta ya está en cola en el servidor: se sigue (o, con el worker apagado, solo se avisa). */
  const minutaEnviada = ({ minutaId, workerHabilitado, origen }) => {
    const apagado = workerHabilitado === false;
    setMinuta({ minutaId, estado: "pendiente", origen, desdeMs: Date.now(), ...(apagado ? { workerApagado: true } : {}) });
    if (apagado) showToast(AVISO_MINUTA_EN_COLA, "warning");
  };

  /** "Ver cambios" de la última minuta aplicada (también después de recargar la página). */
  const verCambiosMinuta = async () => {
    if (!deshacible || !meta || !meta.id || cargandoMinuta) return;
    if (minuta && minuta.minutaId === deshacible.minutaId && minuta.estado === "aplicada") {
      setMinutaOpen(true);
      return;
    }
    setCargandoMinuta(true);
    try {
      const res = (await actaAdmin("estadoMinuta", { actaId: meta.id, minutaId: deshacible.minutaId })) || {};
      if (res.estado !== "aplicada") throw new Error("No encontramos los cambios de esa minuta.");
      setMinuta({ ...res, minutaId: deshacible.minutaId, revisionId: res.revisionId || deshacible.revisionId });
      setMinutaOpen(true);
    } catch (e) {
      showToast(mensajeDeError(e), "error");
    } finally {
      setCargandoMinuta(false);
    }
  };

  const deshacerMinuta = (revisionId) => {
    if (!meta || !meta.id || !revisionId) return;
    const correr = async () => {
      setDeshaciendo(true);
      try {
        await actaAdmin("deshacer", { actaId: meta.id, revisionId });
        await abrir(meta.id, { recarga: true });
        setMinuta((m) => (m && m.revisionId === revisionId ? { ...m, deshecha: true } : m));
        showToast("Se deshicieron los cambios de la minuta.", "success");
      } catch (e) {
        showToast(mensajeDeError(e), "error");
      } finally {
        setDeshaciendo(false);
      }
    };
    if (dirty) {
      setMinutaOpen(false); // un modal por vez
      conGuardia(correr, "Deshacer recarga el acta: se pierden los cambios que no guardaste.");
    } else {
      correr();
    }
  };

  // ── Aprobar y publicar ──
  /**
   * N° que se muestra al confirmar: el que propone el servidor (pdfSubida lo
   * devuelve sin reservarlo y sigue el contador del edificio, por ejemplo el
   * de un libro de actas en papel). Sin acta guardada, o si falla, null.
   */
  const numeroDelServidor = async () => {
    if (!meta || !meta.id) return null;
    try {
      const r = await actaAdmin("pdfSubida", { actaId: meta.id, version: meta.version });
      return r && r.actaNumero ? String(r.actaNumero) : null;
    } catch (_) {
      return null;
    }
  };

  const abrirPublicar = async () => {
    if (soloLectura || !puedePublicar) return;
    const [actas, propuesto] = await Promise.all([cargarLista(), numeroDelServidor()]);
    // Si el servidor no lo dijo, la estimación con las actas publicadas.
    setNumeroPrevisto(propuesto || proximoNumeroActa(actas || lista));
    setProgresoPub(null);
    setPublicarOpen(true);
  };

  const chequeoPublicar = useMemo(
    () => (publicarOpen && acta && padron ? revisarParaPublicar(acta, padron.edificio, revision) : null),
    [publicarOpen, acta, padron, revision]
  );

  const publicar = async () => {
    if (publicando || !acta || !padron || !puedePublicar) return;
    setPublicando(true);
    let ref = meta && meta.id ? { id: meta.id, version: meta.version } : null;
    try {
      // 1) Lo que falta guardar se guarda tal cual. El número NO se guarda acá:
      //    lo fija el servidor al publicar, así un fallo en el medio no deja un
      //    borrador con un número que nunca se publicó.
      if (!ref || dirty) {
        setProgresoPub({ paso: "Guardando el acta…" });
        ref = await guardarData(acta, { silencioso: true });
        if (!ref) {
          setPublicarOpen(false);
          return;
        }
      }
      // El servidor recalcula los resultados al publicar; estos van por compatibilidad.
      const resultadosSnapshot = calcularResultadosSnapshot(acta, padron.edificio);

      let numero = numeroPrevisto;
      let res = null;
      for (let intento = 1; !res; intento += 1) {
        // 2) URL firmada para el PDF de esta versión y el número que le toca ahora.
        setProgresoPub({ paso: "Preparando el PDF…" });
        const sub = await actaAdmin("pdfSubida", { actaId: ref.id, version: ref.version });
        if (!sub || !sub.uploadUrl || !sub.path) throw new Error("El servidor no devolvió dónde subir el PDF.");
        if (sub.actaNumero) numero = String(sub.actaNumero);
        if (!numero) throw new Error("No pudimos saber qué número le corresponde al acta.");
        const impreso = numero;
        setNumeroPrevisto(impreso);

        // 3) PDF de la vista previa con ese número pintado (solo en pantalla).
        setProgresoPub({ paso: `Generando el PDF del acta N° ${impreso}…` });
        flushSync(() => setNumeroImpresion(impreso));
        const blob = await conCaptura((el) => generarPresupuestoPdf(el, nombrePdf(acta, impreso), { download: false }));
        if (!blob || !blob.size) throw new Error("No se pudo generar el PDF.");
        if (blob.size > MAX_PDF_BYTES) throw new Error("El PDF quedó demasiado pesado (más de 15 MB).");

        // 4) Subida por la URL firmada.
        setProgresoPub({ paso: "Subiendo el PDF…", pct: 0 });
        await subirAUrlFirmada(sub.uploadUrl, blob, {
          contentType: "application/pdf",
          headers: sub.headers || {},
          onProgreso: (pct) => setProgresoPub({ paso: "Subiendo el PDF…", pct }),
        });

        // 5) Publicar con el número impreso: el servidor lo verifica, asigna,
        //    congela padrón y resultados y avisa a los propietarios.
        setProgresoPub({ paso: "Publicando y avisando a los propietarios…" });
        try {
          res =
            (await actaAdmin("publicar", {
              actaId: ref.id,
              version: ref.version,
              pdfPath: sub.path,
              resultadosSnapshot,
              actaNumeroImpreso: impreso,
            })) || {};
        } catch (e) {
          // Otra acta del edificio se publicó en el medio y se llevó ese número:
          // se genera el PDF otra vez con el que corresponde ahora.
          if (!esNumeroDesactualizado(e) || intento >= MAX_INTENTOS_NUMERO) throw e;
          const nuevo = String(detallesDeError(e).actaNumero || "");
          showToast(
            `Otra acta del edificio se publicó con el N° ${impreso} mientras tanto: generamos el PDF de nuevo${nuevo ? ` con el N° ${nuevo}` : ""}.`,
            "warning"
          );
          if (nuevo) numero = nuevo;
        }
      }

      const asignado = res.actaNumero ? String(res.actaNumero) : numero;
      const recargada = await abrir(ref.id, { recarga: true });
      if (!recargada) {
        // Publicada en el servidor aunque no se pudo recargar: se bloquea igual.
        setMeta((x) => ({ ...x, status: "publicada", actaNumero: asignado, publicadaEnMs: Date.now() }));
        setActa((a) => (a ? { ...a, actaNumero: asignado } : a));
      }
      cargarLista();
      setPublicarOpen(false);
      const envio = res.envio || {};
      const avisos =
        Number(envio.total) > 0
          ? ` Avisos: ${Number(envio.enviados) || 0} de ${envio.total} enviados${Number(envio.fallidos) > 0 ? `, ${envio.fallidos} con error` : ""}.`
          : "";
      showToast(`Acta${asignado ? ` N° ${asignado}` : ""} publicada.${avisos}`, "success");
      // No debería pasar (publicar verifica el número impreso), pero si pasa que se sepa.
      if (res.actaNumero && numero && String(res.actaNumero) !== String(numero)) {
        showToast(
          `Atención: el número asignado (N° ${res.actaNumero}) no coincide con el impreso en el PDF (N° ${numero}). Avisá a soporte.`,
          "warning"
        );
      }
    } catch (e) {
      if (ref && esConflictoDeVersion(e)) {
        setPublicarOpen(false);
        await abrir(ref.id, { recarga: true });
        setAvisoConflicto(AVISO_CONFLICTO);
        showToast("El acta cambió mientras la publicabas: revisala y probá de nuevo.", "warning");
      } else if (ref && codigoDeError(e) === "deadline-exceeded") {
        // La llamada tardó, pero el servidor puede haber terminado: se mira el estado real.
        const m = await abrir(ref.id, { recarga: true });
        if (m && m.status === "publicada") {
          setPublicarOpen(false);
          showToast(`Acta${m.actaNumero ? ` N° ${m.actaNumero}` : ""} publicada. Los avisos siguen saliendo.`, "success");
        } else {
          showToast("La publicación tardó demasiado. Revisá en unos minutos si quedó publicada.", "warning");
        }
      } else {
        revisarSiTrabaProcesamiento(e);
        if (detallesDeError(e).motivo === "procesando") setPublicarOpen(false); // que se vea el aviso de la cola
        showToast(mensajeDeError(e), "error");
      }
    } finally {
      setNumeroImpresion(null);
      setPublicando(false);
      setProgresoPub(null);
    }
  };

  // ── Ir a un punto desde el panel de revisión ──
  const irAPunto = (puntoId) => {
    setTab("editar");
    setTimeout(() => {
      const el = document.getElementById(`acta-punto-${puntoId}`);
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const presentes = useMemo(() => (acta ? acta.asistencia.filter((r) => r.presente).length : 0), [acta]);

  // ── Render ──
  const pageStyle = embebido ? { ...S.page, ...S.pageEmbebido } : S.page;

  if (!selectedBuilding) {
    return (
      <div style={pageStyle}>
        <ScreenHeader icon={FaFileContract} title="Actas de Asamblea" subtitle="Crear y editar actas de asamblea de copropietarios" />
        <div style={S.emptyBox}>Seleccioná un edificio para crear o editar sus actas de asamblea.</div>
      </div>
    );
  }

  if (cargandoPadron || !padron || !acta || !meta) {
    return (
      <div style={pageStyle}>
        <ScreenHeader icon={FaFileContract} title="Actas de Asamblea" subtitle={selectedBuilding.name} />
        <Loader />
      </div>
    );
  }

  const est = estadoActa(meta.status);
  const botonChico = { padding: "8px 16px", fontSize: typography.sizes.base };
  // Audio de la asamblea en cola sin worker: el acta queda trabada hasta que se cancele.
  const colaSinWorker =
    !!procAsamblea && !publicada && !!meta.asambleaId && procAsamblea.aid === meta.asambleaId && puedeCancelarProcesamiento(procAsamblea);
  // Publicada: el número que asignó el servidor. Si no, el que tiene el acta
  // (el que se cargó a mano sin el módulo; con el módulo, un acta nueva no
  // tiene número hasta publicarse).
  const numeroTitulo = publicada ? meta.actaNumero : acta.actaNumero;

  return (
    <div style={pageStyle}>
      <div style={S.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[3], minWidth: 0 }}>
          {embebido && (
            <button
              type="button"
              style={{ ...btnSecondary, ...botonChico }}
              className="habitia-btn-secondary"
              onClick={() => conGuardia(onVolver)}
            >
              <FaArrowLeft size={12} /> Volver a la asamblea
            </button>
          )}
          <ScreenHeader
            icon={FaFileContract}
            title={numeroTitulo ? `Acta N° ${numeroTitulo}` : "Acta de Asamblea"}
            subtitle={`${selectedBuilding.name} · ${presentes}/${acta.asistencia.length} presentes`}
            size="compact"
          />
          <StatusBadge status={est.badge} label={est.label} size="sm" />
        </div>
        <div style={S.actions}>
          {!embebido && buildings && buildings.length > 1 && (
            <select
              value={selectedBuilding.buildingid}
              onChange={(e) => handleSelectBuilding(e.target.value)}
              style={S.buildingSelect}
              aria-label="Seleccionar edificio"
            >
              {buildings.map((b) => (
                <option key={b.buildingid} value={b.buildingid}>
                  {b.name || `Edificio ${b.buildingid}`}
                </option>
              ))}
            </select>
          )}
          {!publicada && !meta.asambleaId && (
            <button type="button" style={S.btnAi} className="habitia-btn-secondary" onClick={() => setAiOpen(true)} disabled={soloLectura}>
              <FaMagic size={13} /> Asistente IA
            </button>
          )}
          {!publicada && meta.id && meta.asambleaId && puedePublicar && (
            <button type="button" style={S.btnAi} className="habitia-btn-secondary" onClick={() => setMinutaOpen(true)} disabled={publicando}>
              <FaMagic size={13} />{" "}
              {minutaActiva
                ? enColaLocal(minuta)
                  ? "Minuta en cola (local)"
                  : "Minuta: procesando…"
                : minutaEnCola
                  ? "Minuta en cola"
                  : "Ajustar con minuta"}
            </button>
          )}
          {!embebido && (
            <button type="button" style={{ ...btnSecondary, ...botonChico }} className="habitia-btn-secondary" onClick={() => setShowList((s) => !s)} aria-expanded={showList}>
              <FaFolderOpen size={13} /> Actas {lista.length > 0 ? `(${lista.length})` : ""}
            </button>
          )}
          {!embebido && (
            <button type="button" style={{ ...btnSecondary, ...botonChico }} className="habitia-btn-secondary" onClick={() => conGuardia(nuevaLocal)} disabled={publicando}>
              <FaPlus size={13} /> Nueva
            </button>
          )}
          <button type="button" style={{ ...btnSecondary, ...botonChico }} className="habitia-btn-secondary" onClick={() => descargarPdf()} disabled={generando || publicando}>
            <FaDownload size={13} /> {generando ? "Generando…" : publicada ? (meta.firmada ? "Descargar generada" : "Descargar PDF") : "Vista previa PDF"}
          </button>
          {publicada && meta.firmada && (
            <button
              type="button"
              style={{ ...btnSecondary, ...botonChico }}
              className="habitia-btn-secondary"
              onClick={() => descargarPdf({ firmada: true })}
              disabled={generando}
            >
              <FaDownload size={13} /> Descargar firmada
            </button>
          )}
          {publicada && puedePublicar && (
            <>
              <button
                type="button"
                style={{ ...btnPrimary, ...botonChico }}
                className="habitia-btn-primary"
                onClick={() => firmadaRef.current && firmadaRef.current.click()}
                disabled={subiendoFirmada}
              >
                <FaFileSignature size={13} /> {subiendoFirmada ? "Subiendo…" : meta.firmada ? "Reemplazar acta firmada" : "Subir acta firmada"}
              </button>
              <input
                ref={firmadaRef}
                type="file"
                accept="application/pdf,.pdf"
                aria-label="Archivo del acta firmada"
                tabIndex={-1}
                style={S.archivoOculto}
                onChange={(e) => {
                  subirFirmada(e.target.files && e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </>
          )}
          {!publicada && (
            <button
              type="button"
              style={{ ...btnSecondary, ...botonChico }}
              className="habitia-btn-secondary"
              onClick={guardar}
              disabled={guardando || soloLectura || (!dirty && !!meta.id)}
            >
              <FaSave size={13} /> {guardando ? "Guardando…" : meta.id ? "Guardar cambios" : "Guardar"}
            </button>
          )}
          {/* Sin el módulo Asambleas habilitado no se publica: el acta se maneja a mano, como antes. */}
          {!publicada && puedePublicar && (
            <button type="button" style={{ ...btnPrimary, ...botonChico }} className="habitia-btn-primary" onClick={abrirPublicar} disabled={soloLectura || guardando}>
              <FaCheckDouble size={13} /> Aprobar y publicar
            </button>
          )}
        </div>
      </div>

      {avisoConflicto && (
        <div style={S.alerta} role="alert">
          <span style={{ flex: 1 }}>{avisoConflicto}</span>
          <button type="button" style={{ ...btnSecondary, ...botonChico }} className="habitia-btn-secondary" onClick={() => setAvisoConflicto("")}>
            Entendido
          </button>
        </div>
      )}
      {colaSinWorker && (
        <div style={S.alerta} role="alert">
          <span style={{ flex: 1 }}>{AVISO_PROCESAMIENTO_EN_COLA}</span>
          <button
            type="button"
            style={{ ...btnDanger, ...botonChico }}
            className="habitia-btn-danger"
            onClick={pedirCancelarProcesamiento}
            disabled={cancelandoProc}
          >
            {cancelandoProc ? "Cancelando…" : "Cancelar procesamiento"}
          </button>
        </div>
      )}
      {minutaActiva && (
        <div style={S.info} role="status">
          <FaMagic size={12} aria-hidden="true" /> Aplicando la minuta: el acta queda en solo lectura hasta que termine.
        </div>
      )}
      {minutaEnCola && (
        <div style={S.info} role="status">
          <FaMagic size={12} aria-hidden="true" /> Hay una minuta en cola: se aplica cuando se habilite el procesamiento
          automático. Mientras tanto podés seguir editando el acta.
        </div>
      )}
      {deshacible && !minutaActiva && !minutaOpen && !publicando && (
        <div style={S.info} role="status">
          <FaMagic size={12} aria-hidden="true" />
          <span style={{ flex: 1 }}>
            El último cambio del acta lo hizo una minuta{deshacible.enMs ? ` (${fechaHora(deshacible.enMs)})` : ""}.
          </span>
          <button
            type="button"
            style={{ ...btnSecondary, ...botonChico }}
            className="habitia-btn-secondary"
            onClick={verCambiosMinuta}
            disabled={cargandoMinuta || deshaciendo}
          >
            {cargandoMinuta ? "Cargando…" : "Ver cambios"}
          </button>
          <button
            type="button"
            style={{ ...btnSecondary, ...botonChico }}
            className="habitia-btn-secondary"
            onClick={() => deshacerMinuta(deshacible.revisionId)}
            disabled={deshaciendo || cargandoMinuta}
          >
            <FaUndo size={12} /> {deshaciendo ? "Deshaciendo…" : "Deshacer la minuta"}
          </button>
        </div>
      )}

      {!embebido && showList && (
        <div style={S.listPanel}>
          {lista.length === 0 ? (
            <p style={S.listEmpty}>No hay actas guardadas para este edificio.</p>
          ) : (
            lista.map((s) => {
              const e = estadoActa(s.status);
              return (
                <div key={s.id} style={{ ...S.listRow, ...(meta.id === s.id ? S.listRowActive : {}) }}>
                  <button
                    type="button"
                    style={S.listOpen}
                    onClick={() =>
                      conGuardia(() => {
                        // La minuta en curso es de otra acta: se deja de seguir (el servidor la termina igual).
                        if (meta.id !== s.id) setMinuta(null);
                        setAvisoConflicto("");
                        abrir(s.id);
                      })
                    }
                    disabled={abriendo}
                  >
                    <span style={S.listTitle}>
                      {s.actaNumero ? `N° ${s.actaNumero}` : "Acta sin número"} · {s.tipo === "extraordinaria" ? "Extraordinaria" : "Ordinaria"}
                    </span>
                    <span style={S.listMeta}>
                      {s.fecha ? fechaCorta(s.fecha) : "Sin fecha"}
                      {s.origen === "ia" ? " · redactada por la IA" : ""}
                      {s.asambleaId ? " · de una asamblea" : ""}
                    </span>
                  </button>
                  <StatusBadge status={e.badge} label={e.label} size="sm" />
                  {s.status !== "publicada" && (
                    <button
                      type="button"
                      style={S.listDelete}
                      className="habitia-btn-ghost"
                      onClick={() => pedirEliminar(s)}
                      aria-label={s.status === "en_revision" ? "Descartar acta en revisión" : "Eliminar acta"}
                      title={s.status === "en_revision" ? "Descartar" : "Eliminar"}
                    >
                      {s.status === "en_revision" ? <FaUndo size={12} /> : <FaTrash size={12} />}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <div style={S.split}>
        <div style={S.previewPane} ref={previewRef}>
          <div style={S.zoomBar} role="toolbar" aria-label="Zoom de la vista previa">
            <button
              type="button"
              style={S.zoomBtn}
              onClick={() => setZoomModo(acotarZoom(zoom - 0.1))}
              disabled={zoom <= ZOOM_MIN}
              aria-label="Alejar"
              title="Alejar"
            >
              <FaSearchMinus size={12} />
            </button>
            <span style={S.zoomValor} aria-live="polite">{Math.round(zoom * 100)} %</span>
            <button
              type="button"
              style={S.zoomBtn}
              onClick={() => setZoomModo(acotarZoom(zoom + 0.1))}
              disabled={zoom >= ZOOM_MAX}
              aria-label="Acercar"
              title="Acercar"
            >
              <FaSearchPlus size={12} />
            </button>
            <button
              type="button"
              style={{ ...S.zoomBtn, ...(zoomModo === "ancho" ? S.zoomBtnActivo : {}) }}
              onClick={() => setZoomModo("ancho")}
              aria-pressed={zoomModo === "ancho"}
            >
              Ancho
            </button>
            <button
              type="button"
              style={{ ...S.zoomBtn, ...(zoomModo === "pagina" ? S.zoomBtnActivo : {}) }}
              onClick={() => setZoomModo("pagina")}
              aria-pressed={zoomModo === "pagina"}
            >
              Página entera
            </button>
          </div>
          <ActaTemplate acta={actaVista} edificio={padronVista.edificio} copropietarios={padronVista.copropietarios} zoom={zoom} />
        </div>
        {capturando && (
          <div style={S.captura} aria-hidden="true">
            <ActaTemplate ref={templateRef} acta={actaVista} edificio={padronVista.edificio} copropietarios={padronVista.copropietarios} />
          </div>
        )}
        <aside style={S.columna} aria-label="Edición del acta">
          {/* El estado del acta va en la columna: la vista previa usa todo el alto. */}
          <ActaEstadoBanner
            meta={meta}
            acta={acta}
            revision={revision}
            dirty={dirty}
            compacto
            onVerRevision={tieneRevision ? () => setTab("revision") : undefined}
          />
          {tieneRevision && (
            <div style={S.tabs} role="tablist" aria-label="Panel del acta">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "editar"}
                style={{ ...S.tab, ...(tab === "editar" ? S.tabActiva : {}) }}
                onClick={() => setTab("editar")}
              >
                {publicada ? "Acta" : "Editar"}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "revision"}
                style={{ ...S.tab, ...(tab === "revision" ? S.tabActiva : {}) }}
                onClick={() => setTab("revision")}
              >
                Revisión{nMarcas > 0 ? ` (${nMarcas})` : ""}
              </button>
            </div>
          )}
          <div style={S.columnaScroll}>
            {/* Los dos quedan montados: el audio sigue sonando mientras se corrige el texto. */}
            <div style={{ display: tab === "revision" && tieneRevision ? "none" : "block" }}>
              <ActaForm
                acta={acta}
                onChange={setActa}
                edificio={padronVista.edificio}
                copropietarios={padronVista.copropietarios}
                readOnly={soloLectura}
                avisoSoloLectura={
                  publicada
                    ? "Acta publicada: queda bloqueada. Podés descargar el PDF."
                    : minutaActiva
                      ? "Se está aplicando una minuta."
                      : "Publicando…"
                }
                marcasPorPunto={marcasMap}
                numeroAlPublicar={!publicada && puedePublicar}
              />
            </div>
            {tieneRevision && (
              <div style={{ display: tab === "revision" ? "block" : "none" }}>
                {/* key por acta: la URL firmada del audio en caché es de ESTA asamblea. */}
                <ActaRevisionPanel
                  key={meta.id || "nueva"}
                  acta={acta}
                  revision={revision}
                  ia={ia}
                  asambleaId={meta.asambleaId}
                  onIrAPunto={irAPunto}
                  onCambiarMarca={cambiarMarca}
                  readOnly={soloLectura}
                />
              </div>
            )}
          </div>
        </aside>
      </div>

      {!publicada && !meta.asambleaId && (
        <ActaAiAssistant
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          buildingId={buildingId}
          padron={padron}
          acta={acta}
          onApply={(merged) => setActa(merged)}
          showToast={showToast}
        />
      )}

      {meta.id && meta.asambleaId && (
        <ActaMinutaModal
          key={meta.id}
          open={minutaOpen}
          onClose={() => setMinutaOpen(false)}
          actaId={meta.id}
          minuta={minuta}
          acta={acta}
          edificio={padronVista.edificio}
          copropietarios={padronVista.copropietarios}
          asegurarGuardado={asegurarGuardado}
          onEnviada={minutaEnviada}
          onDeshacer={deshacerMinuta}
          puedeDeshacer={puedeDeshacerMinuta}
          deshaciendo={deshaciendo}
          onNueva={() => setMinuta(null)}
        />
      )}

      <ActaPublicarModal
        open={publicarOpen}
        onClose={() => setPublicarOpen(false)}
        chequeo={chequeoPublicar}
        numeroPrevisto={numeroPrevisto}
        edificioNombre={acta.edificioNombre || selectedBuilding.name}
        progreso={progresoPub}
        publicando={publicando}
        onConfirmar={publicar}
      />

      <AdminModal
        open={!!confirmar}
        onClose={() => setConfirmar(null)}
        title={confirmar ? confirmar.titulo : ""}
        size="narrow"
        footer={
          <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%" }}>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => setConfirmar(null)}>
              Volver
            </button>
            <button
              type="button"
              style={confirmar && confirmar.peligro ? btnDanger : btnPrimary}
              className={confirmar && confirmar.peligro ? "habitia-btn-danger" : "habitia-btn-primary"}
              onClick={async () => {
                const accion = confirmar && confirmar.accion;
                setConfirmar(null);
                if (accion) await accion();
              }}
            >
              {confirmar ? confirmar.etiqueta : ""}
            </button>
          </div>
        }
      >
        <p style={{ margin: 0, fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.5 }}>{confirmar ? confirmar.texto : ""}</p>
      </AdminModal>
    </div>
  );
}

const S = {
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    fontFamily: typography.fontFamily,
    fontWeight: 400,
  },
  // Dentro del detalle de la asamblea no hay un contenedor de alto fijo: se le da uno.
  pageEmbebido: {
    height: "calc(100vh - 140px)",
    minHeight: 560,
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflow: "hidden",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[4],
    padding: `${spacing[4]}px ${spacing[5]}px ${spacing[3]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
    flexWrap: "wrap",
  },
  actions: { display: "flex", gap: spacing[2], alignItems: "center", flexWrap: "wrap" },
  buildingSelect: {
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    padding: "8px 10px",
    fontSize: typography.sizes.base,
    color: colors.gray900,
    background: colors.white,
    fontFamily: "inherit",
    maxWidth: 200,
    cursor: "pointer",
  },
  btnAi: {
    display: "inline-flex",
    alignItems: "center",
    gap: spacing[2],
    background: colors.primarySubtle,
    color: colors.primaryDark,
    border: `1px solid ${colors.primaryLight}`,
    borderRadius: radius.sm,
    padding: "8px 16px",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: `all ${transitions.fast}`,
  },
  alerta: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: `${spacing[3]}px ${spacing[5]}px`,
    background: colors.warningLight,
    color: colors.warningDark,
    fontSize: typography.sizes.base,
    borderBottom: `1px solid ${colors.gray200}`,
    flexWrap: "wrap",
  },
  info: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: `${spacing[2]}px ${spacing[5]}px`,
    background: colors.infoLight,
    color: colors.infoDark,
    fontSize: typography.sizes.base,
    borderBottom: `1px solid ${colors.gray200}`,
    flexWrap: "wrap",
  },
  listPanel: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
    padding: `${spacing[2]}px ${spacing[5]}px`,
    background: colors.bgMain,
    borderBottom: `1px solid ${colors.gray200}`,
    maxHeight: 260,
    overflowY: "auto",
  },
  listEmpty: { margin: 0, fontSize: typography.sizes.base, color: colors.gray600 },
  listRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: "4px 8px 4px 12px",
  },
  listRowActive: { border: `1px solid ${colors.primary}`, background: colors.primarySubtle },
  listOpen: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    background: "transparent",
    border: 0,
    cursor: "pointer",
    padding: "6px 0",
    fontFamily: "inherit",
    textAlign: "left",
  },
  listTitle: { fontSize: typography.sizes.base, color: colors.black },
  listMeta: { fontSize: typography.sizes.xs, color: colors.gray600 },
  listDelete: {
    background: "transparent",
    border: 0,
    color: colors.error,
    cursor: "pointer",
    padding: spacing[2],
    borderRadius: radius.sm,
    transition: `all ${transitions.fast}`,
  },
  split: { display: "flex", flex: 1, minHeight: 0, overflow: "hidden" },
  previewPane: { flex: 1, minWidth: 0, overflow: "auto", background: colors.gray200, position: "relative" },
  zoomBar: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: spacing[1],
    padding: `${spacing[2]}px ${spacing[3]}px`,
    background: colors.gray200,
    borderBottom: `1px solid ${colors.gray300}`,
  },
  zoomBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: spacing[1],
    background: colors.white,
    color: colors.gray700,
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    padding: "4px 10px",
    fontSize: typography.sizes.sm,
    fontFamily: typography.fontFamily,
    fontWeight: 400,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  zoomBtnActivo: { background: colors.primarySubtle, color: colors.primaryDark, border: `1px solid ${colors.primaryLight}` },
  zoomValor: { minWidth: 48, textAlign: "center", fontSize: typography.sizes.sm, color: colors.gray700 },
  archivoOculto: { position: "absolute", width: 1, height: 1, opacity: 0, overflow: "hidden", pointerEvents: "none" },
  // Fuera de pantalla (no oculta: html2canvas no pinta lo que tiene visibility: hidden).
  captura: { position: "fixed", left: -100000, top: 0, width: "230mm", pointerEvents: "none" },
  columna: {
    width: 400,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    background: colors.white,
    borderLeft: `1px solid ${colors.gray200}`,
  },
  tabs: { display: "flex", gap: spacing[1], padding: `${spacing[2]}px ${spacing[3]}px 0`, borderBottom: `1px solid ${colors.gray200}` },
  // Sin mezclar "border" con "borderBottomColor": React avisa (y puede pintar mal) al alternar.
  tab: {
    background: "transparent",
    borderTop: 0,
    borderLeft: 0,
    borderRight: 0,
    borderBottom: "2px solid transparent",
    padding: `${spacing[2]}px ${spacing[3]}px`,
    fontSize: typography.sizes.base,
    fontFamily: typography.fontFamily,
    fontWeight: 400,
    color: colors.gray600,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  tabActiva: { color: colors.primaryDark, borderBottom: `2px solid ${colors.primary}` },
  columnaScroll: { flex: 1, minHeight: 0, overflowY: "auto" },
  emptyBox: {
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    padding: spacing[6],
    fontSize: typography.sizes.md,
    color: colors.gray700,
  },
};
